import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Cpu, Wifi, WifiOff, HardDrive, MemoryStick } from "lucide-react";

interface Device {
  deviceId: string;
  friendlyName?: string;
  lastSeen?: string;
  shadow?: {
    state?: {
      reported?: {
        agent_version?: string;
        os?: string;
        resources?: {
          cpuPercent?: number;
          memoryPercent?: number;
          diskPercent?: number;
        };
        local_ip?: string;
        local_ips?: string[];
        shell_version?: { version?: string; repo?: string };
      };
    };
  };
}

export function FleetPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get("/devices")
      .then(({ data }) => {
        setDevices(data.devices ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message);
        setLoading(false);
      });
  }, []);

  const isOnline = (d: Device) => {
    if (!d.lastSeen) return false;
    const diff = Date.now() - new Date(d.lastSeen).getTime();
    return diff < 120_000; // 2 minutes
  };

  return (
    <div className="px-8 pt-8 pb-6">
      <h1 className="text-2xl font-semibold text-surface-900">Fleet Dashboard</h1>
      <p className="mt-1 text-sm text-surface-500">
        {loading ? "Loading devices..." : `${devices.length} device${devices.length !== 1 ? "s" : ""} registered`}
      </p>

      {error && (
        <div className="mt-4 px-4 py-3 rounded-lg bg-danger-100 text-danger-500 text-sm">
          {error}
        </div>
      )}

      {!loading && devices.length === 0 && !error && (
        <div className="mt-8 text-center py-16 bg-surface-0 rounded-xl border border-surface-200">
          <Cpu size={40} className="mx-auto text-surface-400 mb-4" strokeWidth={1.2} />
          <h2 className="text-lg font-medium text-surface-800">No devices yet</h2>
          <p className="mt-2 text-sm text-surface-500 max-w-sm mx-auto">
            Follow the Setup Guide to provision your first Raspberry Pi device.
          </p>
        </div>
      )}

      <div className="mt-6 grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {devices.map((device) => {
          const online = isOnline(device);
          const reported = device.shadow?.state?.reported;
          const resources = reported?.resources;

          return (
            <div
              key={device.deviceId}
              className="bg-surface-0 rounded-xl border border-surface-200 p-5 hover:border-surface-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center">
                    <Cpu size={20} className="text-brand-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-surface-900">
                      {device.friendlyName || device.deviceId}
                    </h3>
                    {device.friendlyName && (
                      <p className="text-[11px] text-surface-400 font-mono">{device.deviceId}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {online ? (
                    <>
                      <Wifi size={14} className="text-success-500" />
                      <span className="text-[11px] font-medium text-success-500">Online</span>
                    </>
                  ) : (
                    <>
                      <WifiOff size={14} className="text-surface-400" />
                      <span className="text-[11px] font-medium text-surface-400">Offline</span>
                    </>
                  )}
                </div>
              </div>

              {reported && (
                <div className="mt-4 space-y-2">
                  {reported.os && (
                    <div className="text-xs text-surface-500">{reported.os}</div>
                  )}
                  <div className="flex items-center gap-3 text-xs text-surface-400">
                    {reported.agent_version && (
                      <span>Agent v{reported.agent_version}</span>
                    )}
                    {reported.local_ip && reported.local_ip !== "unknown" && (
                      <span className="font-mono">{reported.local_ip}</span>
                    )}
                  </div>

                  {resources && (
                    <div className="flex gap-4 mt-3">
                      <ResourceBar label="CPU" value={resources.cpuPercent ?? 0} />
                      <ResourceBar label="MEM" value={resources.memoryPercent ?? 0} />
                      <ResourceBar label="DISK" value={resources.diskPercent ?? 0} />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResourceBar({ label, value }: { label: string; value: number }) {
  const color =
    value > 90 ? "bg-danger-500" : value > 70 ? "bg-warning-500" : "bg-success-500";

  return (
    <div className="flex-1">
      <div className="flex justify-between text-[10px] text-surface-400 font-mono mb-1">
        <span>{label}</span>
        <span>{value}%</span>
      </div>
      <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

